import { memo, useState, useCallback, useEffect } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { TextField, Drawer, Box, Button, Typography } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
// Local files
import DrawerSkeleton from 'components/Common/BaseDrawerSkeleton/BaseDrawerSkeleton';
import BaseEventsList from 'components/Analyses/Common/BaseEventsList/BaseEventsList';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useCustomSelector from 'hooks/useCustomSelector';
import useVoiceAnalyses from 'hooks/useVoiceAnalyses';
import useEvents from 'hooks/useEvents';
import { eventsSelectors } from "features/events";
import { voiceAnalysesSelectors } from 'features/voiceAnalyses';

const VoiceAnalysisDrawer = () => {
  const mediaDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const { voice_analysis_id } = useParams();
  const { getVoiceAnalysis, clearLocalActiveVoiceAnalysis } = useVoiceAnalyses();
  const { getVoiceAnalysesEvents, clearLocalAnalysesEvents } = useEvents();
  const { data, pagination } = useCustomSelector(eventsSelectors.getAnalysesEventsData);
  const eventsFetching = useCustomSelector(eventsSelectors.analysesEventsIsFetching);
  const voiceAnalysisFetching = useCustomSelector(voiceAnalysesSelectors.voiceAnalysisIsFetching);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [result, setResult] = useState('');
  const [voice, setVoice] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [firstTimeEventsOpened, setFirstTimeEventsOpened] = useState(true);

  const fetchVoiceAnalysis = useCallback((id) => {
    setOpen(true);
    getVoiceAnalysis({ id })
    .then(({ payload, error }) => {
      if (!error) {
        const { data: { voice_analysis } } = payload;
        handleSetData(voice_analysis);
      }
    })
  }, [getVoiceAnalysis]);

  const handleSetData = (voice_analysis) => {
    setInput(voice_analysis?.input);
    setResult(voice_analysis?.result);
    setVoice(voice_analysis?.voice);
  };

  const handleExiting = () => {
    setInput('');
    setResult('');
    setVoice(null);
    setExpanded(false);
    setFirstTimeEventsOpened(true);
    navigate(`/flow/fl12v`);
    clearLocalActiveVoiceAnalysis();
    clearLocalAnalysesEvents();
  };

  const handleClose = () => setOpen(false);

  const fetchVoiceAnalysesEvents = useCallback((offset, voice_analysis_id) => {
    getVoiceAnalysesEvents({ offset, voice_analysis_id })
  }, [ getVoiceAnalysesEvents]);

  const handleChangeExpand = (_, isExpanded) => {
    setExpanded(isExpanded);
    if (firstTimeEventsOpened) {
      fetchVoiceAnalysesEvents(0, voice_analysis_id);
      setFirstTimeEventsOpened(false);
    }
  }

  useEffect(() => {
    if (!!voice_analysis_id) fetchVoiceAnalysis(voice_analysis_id);
  }, [voice_analysis_id, fetchVoiceAnalysis]);

  return (
    <Drawer
      anchor={!mediaDesktop ? 'bottom' : 'right'}
      open={open}
      SlideProps={{ onExited: handleExiting }}
      onClose={handleClose}
      ModalProps={{ sx: { zIndex: 1300 } }}
      PaperProps={{
        sx: {
          height: '100%',
          width: { xs: '100%', md: '600px'},
          backgroundColor: 'background.default'
        }
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', p: '8px 8px 0' }}>
          <Button color='inherit' sx={{ fontWeight: 700 }} onClick={handleClose}>Close</Button>
          <div style={{ flexGrow: 1 }}/>
        </Box>
        <Box sx={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center'}}>
            Request log
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: '16px 100px 32px' } }}>
      { voiceAnalysisFetching ? <DrawerSkeleton /> :
        <>
          <Typography variant='body1' sx={{ fontWeight: 600, textAlign: 'center', mb: '10px' }}>Job Id: {voice_analysis_id}</Typography>  
          {!!voice?.url && <Box display='flex' justifyContent='center'>
            <audio controls controlsList="nodownload">
              <source src={voice.url} type="audio/wav"/>
              Your browser does not support the audio element.
            </audio>
          </Box>}
          <TextField
            fullWidth
            margin='normal'
            label='Input'
            multiline
            minRows={5}
            maxRows={8}
            disabled
            value={input ?? ''}
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#073842",
              }
            }}
          />
          <TextField
            fullWidth
            margin='normal'
            label='Result'
            multiline
            minRows={5}
            maxRows={8}
            disabled
            value={result ?? ''}
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#073842",
              }
            }}
          />
          <BaseEventsList
            data={data}
            pagination={pagination}
            analysisId={voice_analysis_id}
            eventsFetching={eventsFetching}
            fetchEvents={fetchVoiceAnalysesEvents}
            expanded={expanded}
            handleChangeExpand={handleChangeExpand}
          />
        </>}
      </Box>
    </Drawer>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <VoiceAnalysisDrawer {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);